import { useCallback, useEffect, useState } from 'react';
import Hls from 'hls.js';

/** A single selectable rendition, derived from `hls.levels`. */
export interface QualityLevel {
  /** Index into `hls.levels` — pass to `setLevel`. */
  index: number;
  /** Vertical resolution in px (undefined for audio-only levels). */
  height?: number;
  /** Peak bitrate in bps; used as a label fallback when height is missing. */
  bitrate: number;
}

export interface UseHlsQualityResult {
  levels: QualityLevel[];
  /** Active level index; -1 means Auto (ABR-controlled). */
  current: number;
  /** Force a specific rendition (or -1 for Auto). */
  setLevel: (index: number) => void;
}

function readLevels(hls: Hls | null): QualityLevel[] {
  if (!hls?.levels) return [];
  return hls.levels.map((lvl, index) => ({
    index,
    ...(typeof lvl.height === 'number' ? { height: lvl.height } : {}),
    bitrate: lvl.bitrate,
  }));
}

/**
 * Bridges hls.js rendition state into React. `levels` mirrors `hls.levels`,
 * `current` mirrors `hls.currentLevel` (-1 = Auto), and `setLevel` drives
 * `hls.currentLevel`. Re-reads on LEVELS_UPDATED (manifest refresh) and
 * LEVEL_SWITCHED (ABR or manual change) so the UI stays in sync. No-op surface
 * when `hls` is null (e.g. the progressive-MP4 sports-VOD path).
 */
export function useHlsQuality(hls: Hls | null): UseHlsQualityResult {
  const [levels, setLevels] = useState<QualityLevel[]>(() => readLevels(hls));
  const [current, setCurrent] = useState<number>(hls?.currentLevel ?? -1);

  useEffect(() => {
    if (!hls) {
      setLevels([]);
      setCurrent(-1);
      return undefined;
    }
    const sync = (): void => {
      setLevels(readLevels(hls));
      setCurrent(hls.currentLevel);
    };
    sync();
    hls.on(Hls.Events.LEVELS_UPDATED, sync);
    hls.on(Hls.Events.LEVEL_SWITCHED, sync);
    return (): void => {
      hls.off(Hls.Events.LEVELS_UPDATED, sync);
      hls.off(Hls.Events.LEVEL_SWITCHED, sync);
    };
  }, [hls]);

  const setLevel = useCallback((index: number): void => {
    if (!hls) return;
    hls.currentLevel = index;
    setCurrent(index);
  }, [hls]);

  return { levels, current, setLevel };
}
